import { authFetch, ensureFreshAccess, refreshTokens } from './auth';
/**
 * exportVector.js — client for the lossless vector export path.
 *
 * Implements "Mono Export Vector — Frontend Integration Contract v1.1":
 *   POST /export/vector    — carton PDFs → die detection → MNE placements (JSON)
 *   POST /export/preflight — carton PDFs → per-carton RGB color audit
 *
 * Design notes that this module enforces so callers can't get them wrong:
 *
 *  1. Counts are POSITIONAL (contract §3.2). The Nth `?count=` applies to the
 *     Nth `files` part. The only safe way to guarantee that is to build the
 *     query string and the FormData from the *same array in the same loop* —
 *     which is exactly what buildVectorRequest() does. Callers pass a single
 *     `cartons` array of { file, count }; they never assemble the two halves
 *     themselves.
 *
 *  2. The field name for every uploaded PDF must be `files` (both endpoints).
 *
 *  3. /export/vector returns placements JSON in the MNE coordinate frame
 *     (Y-up, mm, origin = sheet bottom-left). It does NOT return a PDF. The
 *     response is shape-compatible with what LayoutView already renders.
 *
 *  4. Error `detail` may be a string (400), a Pydantic array (422), or — for
 *     prepress — an object `{ code, message, piece }` (reply §2.1).
 *     parseErrorDetail() normalizes all three (contract §7).
 *
 * This module is pure data/transport — no React, no DOM. It is unit-testable in Node.
 */

import {
  serializePrepress, serializePrepressConfigList, serializeCartonType,
  serializeRequired,
  prepressErrorLine,
  placementCartonCount,
} from './prepress';
import {
  prepressChains, blankToSheet, artworkStamps, roundMatrix, placedCuts,
} from './artworkPlacement';

/** 9 dp — far below any press tolerance, and keeps the wire readable. */
const round9 = (v) => Math.round(v * 1e9) / 1e9;

// Keep the base URL in lockstep with App.js. Single source of truth lives there;
// callers pass it in so this module never hard-codes an environment.
//   getVectorPlacements({ apiBase, cartons, sheet, margins })

/* ------------------------------------------------------------------ */
/* Error handling (contract §7)                                        */
/* ------------------------------------------------------------------ */

/**
 * Normalize FastAPI error bodies. `detail` now has THREE shapes:
 *   - array   → Pydantic 422
 *   - string  → plain 400
 *   - object  → prepress 400, `{ code, message, piece }` (reply §2.1)
 *
 * The object shape is checked FIRST. Without it the object fell through every
 * branch and this returned the literal string 'Unknown error', discarding both
 * the cause and the name of the offending carton — a worse operator experience
 * than the bare string it replaced.
 */
export function parseErrorDetail(body) {
  if (!body) return 'Unknown error';
  const d = body.detail;

  // Prepress errors carry a stable machine-readable code. prepressErrorLine
  // returns null for any body that isn't one, so the legacy paths below are
  // untouched.
  const prepressLine = prepressErrorLine(body);
  if (prepressLine) return prepressLine;

  if (Array.isArray(d)) return d[0]?.msg || 'Validation error';
  if (typeof d === 'string') return d;
  // A prepress-shaped object with an unmapped/absent code still beats
  // 'Unknown error' — the server message always names the piece.
  if (d && typeof d === 'object' && typeof d.message === 'string' && d.message) return d.message;
  if (typeof body === 'string') return body;
  return 'Unknown error';
}

/** Thrown for any non-2xx response. Carries the HTTP status so the UI can branch. */
export class VectorApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'VectorApiError';
    this.status = status;     // 400 | 422 | 500 | 503 | ...
    this.body = body;         // raw parsed JSON body, if any
  }
}

async function readError(resp) {
  let body = null;
  try { body = await resp.json(); } catch { /* non-JSON body */ }
  return new VectorApiError(parseErrorDetail(body), resp.status, body);
}

/* ------------------------------------------------------------------ */
/* Spacing (contract 1.11 — query_params.spacing)                      */
/* ------------------------------------------------------------------ */

/**
 * The clear space one carton asks for around it, mm — the contract's
 * `query_params.spacing.range_mm` upper bound. The printer's own layouts leave
 * 7.5 mm (2.5 bleed + 2.5 shared mark lane + 2.5 bleed); the default is 0.
 */
export const SPACING_MAX_MM = 20;

/** A spacing value as the backend accepts it: 0..20 mm, anything else 0. */
export function clampSpacing(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(SPACING_MAX_MM, n);
}

/**
 * Serialize `?spacing=` — the shared broadcast/positional rule. Null when
 * every carton is 0, so a job without spacing sends no parameter and hashes
 * exactly as it did before spacing existed (same job_id). One value when all
 * agree; otherwise a JSON list aligned to file order.
 */
export function serializeSpacing(values) {
  const vals = (Array.isArray(values) ? values : []).map(clampSpacing);
  if (!vals.length || vals.every((v) => v === 0)) return null;
  if (vals.every((v) => v === vals[0])) return String(vals[0]);
  return JSON.stringify(vals);
}

/**
 * The kerf a result was actually nested with. The engine's kerf is ONE value
 * per sheet, so the backend nests at the largest of the kerf field and every
 * carton's spacing and says so in `kerf_effective_mm`. Clearance checks on
 * that sheet — the Layout Editor's "too close" — must use it; the field the
 * operator typed is only a floor. An older backend reports `sheet.kerf`.
 */
export function effectiveKerf(result, sheetKerf = 0) {
  const vals = [Number(sheetKerf), Number(result?.kerf_effective_mm), Number(result?.sheet?.kerf)];
  return Math.max(0, ...vals.filter((v) => Number.isFinite(v)));
}

/* ------------------------------------------------------------------ */
/* Request building (contract §3.2)                                    */
/* ------------------------------------------------------------------ */

/**
 * Build the URL query string and FormData for POST /export/vector from a
 * single cartons array, guaranteeing file/count order stays in sync.
 *
 * @param {Object}   args
 * @param {Array<{file: File, count: number, prepress?: string|null}>} args.cartons
 *        `prepress` is the per-carton mode ('plain'|'pair'|'auto') or null/absent
 *        for an untagged carton. Omit it everywhere → today's behaviour exactly.
 * @param {{w:number,h:number}} args.sheet
 * @param {number} [args.kerf]    Cut clearance between dies, mm (v1.2 §6). Omitted when 0.
 * @param {{bottom?:number,top?:number,left?:number,right?:number}} [args.margins]
 * @returns {{ query: URLSearchParams, form: FormData }}
 */
export function buildVectorRequest({
  cartons, sheet, kerf = 0, margins = {},
  prepressAck = null, offeredCartonTypes = null,
}) {
  if (!Array.isArray(cartons) || cartons.length === 0) {
    throw new Error('buildVectorRequest: cartons must be a non-empty array');
  }

  const query = new URLSearchParams({
    sheet_w: String(sheet.w),
    sheet_h: String(sheet.h),
  });

  // Kerf (v1.2): uniform inter-die spacing in mm. Only append when > 0 so a
  // zero-kerf job hashes identically to a v1.1-era job (kerf is part of the
  // server-side dedup key — contract §3.6).
  const kerfV = Number(kerf || 0);
  if (kerfV > 0) query.set('kerf', String(kerfV));

  // Margins: only append non-zero edges so a zero-margin job hashes identically
  // to a legacy job (contract back-compat guarantee, MNE v3.0 §2).
  for (const edge of ['bottom', 'top', 'left', 'right']) {
    const v = Number(margins[edge] || 0);
    if (v > 0) query.set(`margin_${edge}`, String(v));
  }

  const form = new FormData();

  // CRITICAL: append count and file in the SAME iteration so the Nth count
  // always lines up with the Nth file. Do not refactor into two loops.
  // Prepress modes are collected here for the same reason — a parallel array
  // built elsewhere can drift out of file order; this one cannot.
  const prepressModes = [];
  const requiredFlags = [];
  const prepressConfigs = [];
  const cartonTypes = [];
  const spacings = [];
  cartons.forEach(({ file, count, prepress, required, prepressConfig, cartonType, spacingMm }) => {
    query.append('count', String(count));   // positional — tracks file order
    form.append('files', file);             // field name MUST be "files"
    prepressModes.push(prepress ?? null);
    // ⑭d: `required` is TOP-LEVEL on the piece, never inside the prepress
    // block — sent there it is silently unread, which is why the backend
    // rejects it (400 RequiredFlagMisplaced). Collected in the same loop as
    // the file for the same reason as `count`: alignment is structural here
    // and a convention anywhere else.
    requiredFlags.push(required === true);
    // ⑭e: per-carton config and carton type join the same loop for the same
    // reason. The config list is POSITIONAL and a wrong length is a 400, so
    // building it anywhere else is building a parallel array that can drift.
    prepressConfigs.push(prepressConfig || null);
    cartonTypes.push(cartonType || null);
    // 1.11: spacing is positional too — same loop, same reason.
    spacings.push(clampSpacing(spacingMm));
  });

  // ⑭h / rev 8 §1: `allowed_rotations` is NOT sent. It is engine policy,
  // resolved by the backend from deployment configuration, and a request that
  // sets it is 400 EnginePolicyNotAccepted. ⑬h's structural fix stays — one
  // builder, both routes, a source-shape guard — only the destination is gone.
  // The default is unset on their side too, so nothing re-nests: every job we
  // have ever sent omitted this key.

  // Prepress (contract §2b): a bare mode applies to every file; a JSON list
  // aligns to file order. Returns null when nothing is tagged, so an untagged
  // job sends no parameter at all and behaves exactly as it does today.
  const prepressParam = serializePrepress(prepressModes);
  if (prepressParam !== null) query.set('prepress', prepressParam);

  // Per-carton config (rev 5 §4.2, rev 7 §3) — the POSITIONAL LIST form, and
  // only that form. Still sent alongside a mode: config without a mode is
  // meaningless, and additionalProperties:false makes any stray key a 400
  // ConfigError rather than a silent ignore.
  if (prepressParam !== null) {
    const cfg = serializePrepressConfigList(prepressConfigs);
    if (cfg !== null) query.set('prepress_config', cfg);
  }

  // ⑭e / rev 7 §2: `carton_type` follows the same broadcast/positional rule.
  // Omitted entirely while the deployment accepts a single type — see
  // serializeCartonType for why sending the only value would be a regression.
  const typeParam = serializeCartonType(cartonTypes, offeredCartonTypes);
  if (typeParam !== null) query.set('carton_type', typeParam);

  // 1.11: per-carton spacing. Omitted when every carton is 0.
  const spacingParam = serializeSpacing(spacings);
  if (spacingParam !== null) query.set('spacing', spacingParam);

  // ⑭c: the confirmation token from stage 1 (rev 5 §7.2). Echoed, never
  // regenerated, and deliberately NOT a boolean — a boolean would acknowledge
  // whatever warnings happen to exist at resubmit time, so an edit between
  // stages would silently suppress a DIFFERENT warning, which is the exact
  // failure the handshake exists to prevent. A stale token halts again.
  if (typeof prepressAck === 'string' && prepressAck) query.set('prepress_ack', prepressAck);

  // ⑭d: firm order lines. Encoded like `prepress`, NOT like `allowed_rotations`
  // — the scalar is a boolean, so a flat list is positional. Omitted entirely
  // when nothing is required, so an ordinary job's content hash is unchanged.
  const requiredParam = serializeRequired(requiredFlags);
  if (requiredParam !== null) query.set('required', requiredParam);

  return { query, form };
}

/**
 * Assemble the full URL and body for an export route.
 *
 * This exists so that the one property which must hold — *every* argument a
 * caller supplies reaches the query string* — is a PURE value that a test can
 * read, instead of a side effect buried inside a transport wrapper.
 *
 * ⑬h: `allowedRotations` and `prepressConfig` were built correctly by
 * buildVectorRequest and then silently discarded for six PRs, because each
 * wrapper destructured a fixed key set and forwarded only those keys. The
 * serializer was pinned by 352 assertions; the plumbing between App.js and the
 * query string was never crossed by a test at all. So the fix is structural,
 * not a line: the wrappers below take `args` WHOLE and never name a request
 * parameter, which means a field added to buildVectorRequest reaches the wire
 * on both routes with no further edit and no way to forget one.
 *
 * @param {string} route  '/export/vector' | '/export/vector/async'
 * @param {Object} args   the full caller args (see buildVectorRequest)
 * @returns {{ url: string, form: FormData, query: URLSearchParams }}
 */
export function vectorRequestUrl(route, args) {
  const { query, form } = buildVectorRequest(args);
  return { url: `${args.apiBase}${route}?${query.toString()}`, form, query };
}

/* ------------------------------------------------------------------ */
/* POST /export/vector (contract §3)                                   */
/* ------------------------------------------------------------------ */

/**
 * Upload carton PDFs, let the backend auto-detect die regions and run MNE,
 * and return the placements JSON for canvas preview.
 *
 * The returned object is the full contract §3.3 response:
 *   { job_id, sheet, n_placed, n_unplaced, utilization, overlap_ok,
 *     runtime_ms, sources[], placements[] }
 *
 * @throws {VectorApiError} on non-2xx (status carries the meaning per §7).
 */
export async function getVectorPlacements(args) {
  // ⑬h: `args` is forwarded WHOLE. Destructuring request parameters here is
  // what dropped allowed_rotations and prepress_config — the wrapper decided,
  // silently, which of the caller's fields were allowed to exist.
  const { url, form } = vectorRequestUrl('/export/vector', args);

  const resp = await authFetch(url, {
    method: 'POST',
    body: form,            // do NOT set Content-Type; the browser sets the multipart boundary
    signal: args.signal,
  });

  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* Async export jobs (Frontend Integration Contract v1.5)              */
/*   POST /export/vector/async → { job_id, kind, poll, cancel }        */
/*   Poll/cancel live in the feature namespace /export/vector/jobs/*   */
/*   (kind-scoped: a plan id here 404s). /jobs/* is the global,        */
/*   cross-kind namespace for monitoring.                              */
/*   ⚠ The submit response's poll/cancel URLs ARE the contract —       */
/*     always follow them; never construct job paths by hand.          */
/* ------------------------------------------------------------------ */

/**
 * Job statuses (contract v2.0 §6.2): queued · running · succeeded · failed ·
 * cancelled · interrupted.
 *
 * INVERTED test, same rationale as planner.js: enumerate "still working" and
 * treat everything else — including unknown future statuses — as terminal, so
 * an unrecognised status can never strand the poll loop (§6.2, §A.5).
 */
export const JOB_ACTIVE_STATUSES = ['queued', 'running'];
/** Known terminal statuses — for labels/copy. NOT the terminal test. */
export const JOB_TERMINAL_STATUSES = ['succeeded', 'failed', 'cancelled', 'interrupted'];
export function isJobTerminal(status) { return !JOB_ACTIVE_STATUSES.includes(status); }

/**
 * Start an ASYNC vector layout job — same params + validation as the sync
 * route (bad requests still fail 422 synchronously, before a job exists).
 * Returns { job_id, kind, status, poll, cancel } — v1.5: the poll/cancel URLs
 * point into the feature namespace /export/vector/jobs/* and MUST be followed
 * as returned (they're the contract; namespaces may move again).
 *
 * Job phases (v1.5 §5.2): preparing (0.02) → detecting (0.05–0.50, detail
 * carries files_done/files_total) → nesting (fraction HOLDS at ~0.55 —
 * indeterminate; the engine has no mid-run callback, do not synthesize a
 * moving bar) → assembling (0.95) → done (1.0).
 *
 * A 404/405 from this call means the backend predates the async export route
 * — callers should fall back to the sync getVectorPlacements.
 *
 * @throws {VectorApiError} on non-2xx.
 */
export function startVectorLayoutAsync(args) {
  // ⑬h: same as the sync route — `args` goes to vectorRequestUrl whole. Only
  // the two TRANSPORT concerns are named here; no request parameter is.
  const { signal, onUploadProgress } = args;
  const { url, form } = vectorRequestUrl('/export/vector/async', args);

  // XHR instead of fetch, deliberately: fetch cannot report UPLOAD progress,
  // and the submit is a multi-MB multipart of the carton PDFs — on slow or
  // stalled links (edge/tunnel hiccups) a silent fetch looks frozen. XHR's
  // upload.onprogress lets the UI show honest percentages and lets callers
  // detect a stalled transfer. Return/error contracts are identical to the
  // fetch version (JSON body; VectorApiError with .status on non-2xx).
  //
  // Auth (PR ② §4/§5): the access token is fetched fresh BEFORE each attempt
  // (proactive — a mid-upload expiry would otherwise cost a full re-upload),
  // and a 401(invalid_token) response still gets the standard refresh → retry
  // ONCE, since the retry re-uploads the body.
  const attempt = (retriedAfter401) => new Promise((resolve, reject) => {
    ensureFreshAccess().then((tok) => {
      if (signal && signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.responseType = 'text';
      if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);

      const onAbort = () => xhr.abort();
      const cleanup = () => { if (signal) signal.removeEventListener('abort', onAbort); };
      if (signal) signal.addEventListener('abort', onAbort, { once: true });

      if (xhr.upload && typeof onUploadProgress === 'function') {
        xhr.upload.onprogress = (ev) => {
          onUploadProgress({ loaded: ev.loaded, total: ev.lengthComputable ? ev.total : null });
        };
      }

      xhr.onload = () => {
        cleanup();
        let body = null;
        try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          if (body) resolve(body);
          else reject(new VectorApiError('Malformed response from /export/vector/async', xhr.status, null));
        } else if (xhr.status === 401 && body?.detail?.code === 'invalid_token' && !retriedAfter401) {
          // §5: refresh, then retry the submit once (re-uploads the body).
          refreshTokens()
            .then(() => resolve(attempt(true)))
            .catch(() => reject(new VectorApiError(parseErrorDetail(body), xhr.status, body)));
        } else {
          reject(new VectorApiError(parseErrorDetail(body), xhr.status, body));
        }
      };
      xhr.onerror = () => { cleanup(); reject(new VectorApiError('Network error while submitting the layout job.', 0, null)); };
      xhr.ontimeout = () => { cleanup(); reject(new VectorApiError('Timed out while submitting the layout job.', 0, null)); };
      xhr.onabort = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };

      xhr.send(form);
    }, reject);
  });

  if (signal && signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  return attempt(false);
}

/**
 * Poll a job. ALWAYS prefer the ready-made `poll` URL from the submit response
 * (v1.5: kind-scoped feature namespace). The `/jobs/{id}` fallback is the
 * GLOBAL cross-kind namespace — valid for any job id, but only used here when
 * no poll URL was given. On `succeeded`, `result` is the SAME shape as the
 * sync route (one shared pipeline). On `cancelled`, `result` is null by
 * contract. Also carries `elapsed_seconds` and kind-specific progress.detail.
 *
 * @throws {VectorApiError} (404 = unknown in this namespace: reaped, wrong
 *   kind for a feature namespace, or another instance)
 */
export async function pollJob({ apiBase, pollUrl, jobId, signal }) {
  const url = pollUrl ? `${apiBase}${pollUrl}` : `${apiBase}/jobs/${jobId}`;
  const resp = await authFetch(url, { signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* Poll cadence (PR ⑫, landed in ⑭i)                                  */
/* ------------------------------------------------------------------ */

/**
 * The poll schedule, as backend asked for it: ~700 ms while a result may be
 * seconds away, 2 s once it plainly is not, ±10 % jitter throughout.
 *
 * WHY THIS IS ONE FUNCTION AND NOT THREE CONSTANTS
 * ------------------------------------------------
 * There are THREE poll loops in App.js — the plan job, the resume of a running
 * layout, and the live submit — and every one of them was a bare `700`. Three
 * copies of a number is three chances to change two of them. The cadence is now
 * a value a test can read, in the module that owns polling.
 *
 * WHY IT MATTERS AT ALL. A cold `auto` nest is minutes, not seconds. A single
 * layout recorded from the field ran the loop long enough to account for most
 * of a 167-request session on its own — a flat 700 ms is ~85 requests a minute
 * per open tab, for a job whose answer cannot arrive early.
 *
 * WHY JITTER. Every tab watching the same job polls on the same phase without
 * it; a plant floor with six machines open on one job is a synchronised burst
 * against a process that shares its CPU with the nesting run.
 *
 * The fast window is deliberately generous: short jobs are the ones where
 * latency is felt, and they finish inside it.
 */
export const POLL_FAST_MS = 700;
export const POLL_SLOW_MS = 2000;
export const POLL_FAST_WINDOW_MS = 10000;
export const POLL_JITTER = 0.1;

/**
 * Delay before the next poll, given how long this loop has been running.
 *
 * @param {number} elapsedMs  ms since the loop started
 * @param {() => number} [rnd] injectable for the tests; must return [0,1)
 * @returns {number} ms to wait — always ≥ 1, never NaN for a junk input
 */
export function pollDelayMs(elapsedMs, rnd = Math.random) {
  const e = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  const base = e < POLL_FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS;
  const r = typeof rnd === 'function' ? rnd() : 0.5;
  const spread = Number.isFinite(r) ? (r * 2 - 1) * POLL_JITTER : 0;
  return Math.max(1, Math.round(base * (1 + spread)));
}

/**
 * Request cancellation of a job — v1.5 §6: POST the `cancel` URL from the
 * submit response (feature namespace). **202 Accepted** with a snapshot body
 * (`status:"running"`, `cancel_requested:true`); the cancel token is armed
 * into the engine and interrupts the computation mid-flight (~0.03 s), with
 * terminal `cancelled` typically < 1 s away via the poll (2 s is a safe
 * upper bound). Fire-and-forget-then-poll — never flip the UI on this
 * response; branch on the FINAL polled status.
 *
 * A **404** means "unknown in this namespace": the job finished and was
 * reaped (the cancel-vs-completion race — the job may resolve 'succeeded'),
 * the id belongs to another kind, or it runs on another instance. NOT an
 * error — keep polling.
 *
 * @throws {VectorApiError} (404 — see above)
 */
export async function cancelJob({ apiBase, cancelUrl, jobId, signal }) {
  const url = cancelUrl ? `${apiBase}${cancelUrl}` : `${apiBase}/jobs/${jobId}/cancel`;
  const resp = await authFetch(url, { method: 'POST', signal });
  if (!resp.ok) throw await readError(resp);   // resp.ok accepts the 202
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* POST /export/preflight (contract §5)                                */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* The confirmation handshake (rev 6 §2) — PR ⑭c                       */
/* ------------------------------------------------------------------ */

/**
 * Stage 1, bodiless: `POST /planner/prepress/preflight`.
 *
 * Named `prepressPreflight` and NOT `preflight`, because `runPreflight()`
 * below is a different endpoint entirely (`/export/preflight` — RGB and die
 * detection on uploaded PDFs). Same word, unrelated jobs.
 *
 * Costs no upload: names, corner counts and flags only. That is the whole
 * point — a halt gets answered BEFORE a multi-megabyte multipart goes out
 * rather than after it.
 *
 * @returns {{ warnings: Array, ack_token: string|null }}
 */
export async function prepressPreflight({ apiBase, pieces, signal }) {
  const resp = await authFetch(`${apiBase}/planner/prepress/preflight`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pieces }),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Stage 2 for a job that ALREADY halted:
 * `POST /export/vector/async/{job_id}/confirm?prepress_ack=…`
 *
 * The async route persists uploads before queuing, so the PDFs are still on
 * the server — confirming re-queues the stored payload rather than making the
 * operator send them again.
 *
 * Returns a **new** job (`{ job_id, confirmed_from, status, poll }`). Poll the
 * new id; the halted one stays halted.
 *
 * ⑭f / rev 7 §6: `confirmPath` comes from the halt's `error_detail.confirm`
 * and is never built here. This module states twice that the contract is
 * "follow the URLs the response gave you"; `/confirm` was the single place
 * that did not, because a halted job carried no URL to follow. It does now, so
 * the construction is gone rather than kept as a fallback — a fallback would
 * quietly become the live path on exactly the deployments nobody is watching.
 *
 * Validated upstream by `jobPathFromResponse`. A halt without a usable path
 * never reaches here; the caller resubmits with the ack instead (rev 6 §2.2).
 *
 * @throws {VectorApiError} 404 unknown · 409 NotAwaitingConfirmation ·
 *   409 ConfirmRequiresResubmit (no blob store — resubmit with the ack instead)
 */
export async function confirmVectorJob({ apiBase, confirmPath, ackToken, signal }) {
  if (typeof confirmPath !== 'string' || !confirmPath) {
    throw new Error('confirmVectorJob: confirmPath is required — it comes from error_detail.confirm');
  }
  const q = new URLSearchParams({ prepress_ack: ackToken });
  // The path may already carry a query; appending a second `?` would make the
  // ack unreadable and the halt would repeat with no visible cause.
  const sep = confirmPath.includes('?') ? '&' : '?';
  const resp = await authFetch(
    `${apiBase}${confirmPath}${sep}${q.toString()}`,
    { method: 'POST', signal },
  );
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** The deployment kept no uploads — the operator must resubmit, ack in hand. */
export function isConfirmResubmitRequired(body) {
  return body?.detail?.code === 'ConfirmRequiresResubmit';
}

/**
 * `POST /planner/prepress/describe` — one carton, no sheet, no nesting.
 *
 * ⑭e / rev 6 §3: the pre-run source of flap ranges. `plain` and sub-second, so
 * it is cheap enough to call as the operator drags a slider — but backend
 * enforces no rate limit, it shares a process with nesting jobs, and the FIRST
 * call loads `prepress_v1` into that process. So the caller debounces and
 * passes `signal` to abort the previous request: an un-debounced onChange is
 * the one failure mode they named, and an un-aborted one repaints the editor
 * with ranges for a slider position the operator has already left.
 *
 * @throws {VectorApiError} same structured shape as a run, so one error reader
 *   serves the editor and the job.
 */
export async function describePrepressCarton({ apiBase, body, signal }) {
  if (!body || typeof body !== 'object') {
    throw new Error('describePrepressCarton: body is required (see describeRequest)');
  }
  const resp = await authFetch(`${apiBase}/planner/prepress/describe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Audit color spaces in each uploaded PDF. Run this at carton-upload time so
 * the operator sees RGB contamination before nesting setup.
 *
 * @param {File[]} files
 * @returns {Promise<{ results: Array<PreflightResult> }>}  (contract §5.3)
 *
 * PreflightResult = {
 *   source_id, filename, has_rgb, counts{}, ops{ rg, RG, k, K }, summary
 * }
 * @throws {VectorApiError} on non-2xx.
 */
export async function runPreflight({ apiBase, files, signal }) {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('runPreflight: files must be a non-empty array');
  }

  const form = new FormData();
  files.forEach((f) => form.append('files', f));   // field name MUST be "files"

  const resp = await authFetch(`${apiBase}/export/preflight`, {
    method: 'POST',
    body: form,
    signal,
  });

  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* Response interpretation helpers (contract §6, §7)                   */
/* ------------------------------------------------------------------ */

/**
 * Net utilization — efficiency over the usable (post-margin) region.
 * Gross utilization (result.utilization) is over the full sheet (contract §6).
 */
export function netUtilization(result) {
  const { utilization, sheet } = result;
  const m = sheet.margins || {};
  const usableW = sheet.width - (m.left || 0) - (m.right || 0);
  const usableH = sheet.height - (m.bottom || 0) - (m.top || 0);
  if (usableW <= 0 || usableH <= 0) return utilization;
  return utilization * (sheet.width * sheet.height) / (usableW * usableH);
}

/** True if any edge margin is active — gate for showing the net-utilization stat. */
export function hasActiveMargins(result) {
  return Object.values(result?.sheet?.margins || {}).some((v) => v > 0);
}

/**
 * Classify a placements result into a render decision (contract §7).
 * Returns { renderable, level: 'error'|'warning'|'ok', message }.
 *
 *  - overlap_ok === false  → NOT renderable, geometrically invalid.
 *  - n_unplaced > 0        → renderable, but warn.
 *  - otherwise             → ok.
 */
export function interpretVectorResult(result) {
  if (!result || result.overlap_ok === false) {
    return {
      renderable: false,
      level: 'error',
      message: 'Placements overlap — layout is geometrically invalid. Try different counts or a larger sheet.',
    };
  }
  if (result.n_unplaced > 0) {
    return {
      renderable: true,
      level: 'warning',
      message: `${result.n_unplaced} piece(s) could not fit on the sheet. Reduce counts or increase the sheet size.`,
    };
  }
  return { renderable: true, level: 'ok', message: '' };
}

/** Split a preflight response into clean vs RGB-flagged cartons (contract §5.5). */
export function partitionPreflight(results) {
  const clean = [];
  const flagged = [];
  for (const r of results || []) (r.has_rgb ? flagged : clean).push(r);
  return { clean, flagged };
}

/* ------------------------------------------------------------------ */
/* Press-ready PDF export — POST /export/vector/pdf                     */
/* (EXPORT_VECTOR_BACKEND_SPEC v1.0 §5; handover doc)                   */
/* ------------------------------------------------------------------ */

/**
 * Parse the `/export/vector` `die_source` string into the die spec the PDF
 * endpoint expects. Examples: "layer:DIELINE" -> { die_layer: "DIELINE" },
 * "spot:CutContour" -> { die_spot: "CutContour" },
 * "artwork:borders" -> { die_artwork: "borders" } (contract 1.10: a die the
 * backend read from the artwork's own panel borders).
 *
 * Because `/export/vector` already auto-detected and nested this die, re-detection
 * by the same name is guaranteed to resolve — so we never need crop_mm here. An
 * artwork die is re-read the same way; the backend's reading is deterministic.
 */
export function parseDieSource(dieSource) {
  if (typeof dieSource !== 'string') return {};
  const [kind, ...rest] = dieSource.split(':');
  const name = rest.join(':');
  if (kind === 'layer' && name) return { die_layer: name };
  if (kind === 'spot' && name) return { die_spot: name };
  if (kind === 'artwork' && name) return { die_artwork: name };
  return {};
}

/**
 * Build the JSON `payload` for POST /export/vector/pdf from a successful
 * `/export/vector` result (handover §4.2 — placements reduced to Frame 6).
 *
 * The crucial mapping: in the VECTOR path, kerf is inter-die *spacing*, not
 * polygon inflation (contract v1.2 §6), so `placed_vertices` IS the placed die
 * polygon. Its (min_x, min_y) is exactly the lower-left of the placed rotated
 * die bbox — precisely what the PDF endpoint wants for (x_mm, y_mm). No
 * kerf-inset correction is needed.
 *
 * @param {Object} result   the /export/vector response
 * @param {Object} [opts]
 * @param {Object<string,'as_is'|'auto_convert'>} [opts.colorDecisions]
 *        per-source_id color decision (default 'as_is').
 * @param {Object} [opts.outputIntent]  { icc, name } — defaults to ISO Coated v2.
 * @param {boolean} [opts.marks] the printer's carton marks (default on). Only
 *        an explicit `false` turns them off.
 * @param {'skip'|'print'} [opts.marksOnNeighbours] a mark that would land on
 *        another carton or its bleed is left out ('skip', the default) or
 *        drawn anyway.
 * @param {boolean} [opts.bleed] bleed round each carton's PRINTED outline:
 *        its own artwork where that runs past the edge (default on). Only an
 *        explicit `false` clips every carton exactly to its die.
 * @param {boolean} [opts.autoBleed] with bleed on, stretch the edge where the
 *        artwork stops at it (contract 1.12 `auto_bleed`, default on). Only an
 *        explicit `false` keeps the bleed to what the artwork itself carries.
 * @param {boolean} [opts.removeBorders] take each carton's borders off (the
 *        default). Only an explicit `false` keeps them — the payload then says
 *        `borders: 'keep'` and every carton is plated exactly as uploaded.
 * @returns {Object} PressPdfPayload
 */
export function buildPressPdfPayload(result, opts = {}) {
  if (!result?.sources || !result?.placements) {
    throw new Error('buildPressPdfPayload: result must have sources[] and placements[]');
  }
  const colorDecisions = opts.colorDecisions || {};
  const outputIntent = opts.outputIntent || { icc: 'ISOcoated_v2_300_eci.icc', name: 'ISO Coated v2 (ECI)' };
  const marks = { enabled: opts.marks !== false };
  const marksOnNeighbours = opts.marksOnNeighbours === 'print' ? 'print' : 'skip';
  const bleed = opts.bleed !== false;
  const autoBleed = opts.autoBleed !== false;
  const borders = opts.removeBorders === false ? 'keep' : 'remove';

  const sheet = { width: result.sheet.width, height: result.sheet.height };
  if (result.sheet.margins) sheet.margins = result.sheet.margins;

  const sources = result.sources.map((s) => ({
    source_id: s.source_id,
    page_index: 0,
    label: s.filename || s.source_id,
    die: parseDieSource(s.die_source),
    color_decision: colorDecisions[s.source_id] === 'auto_convert' ? 'auto_convert' : 'as_is',
  }));

  // Where the ARTWORK goes, which is not where the die went whenever prepress
  // ran — see artworkPlacement.js. Empty for a plain job, and then every
  // placement below takes the corner form exactly as before.
  const chains = prepressChains(result);

  const placements = result.placements.flatMap((p, pieceIndex) => {
    // `source_id` on the placement, NOT `sources[type_index]`. That index
    // holds only while emitted pieces and uploads are 1:1, and a tile breaks
    // it: one source emits `<n>__pair` AND `<n>`, so the last placement
    // indexes past the end of `sources[]` and this threw on `.source_id` —
    // i.e. a tiled job could be nested and previewed but never plated.
    const src = (typeof p.source_id === 'string' && p.source_id)
      ? { source_id: p.source_id }
      : result.sources[p.type_index];
    if (!src) {
      throw new Error(
        `buildPressPdfPayload: placement type_index ${p.type_index} names no `
        + 'source. A backend that tiles must send source_id on every placement.');
    }
    // A prepress carton's nested piece is the flapped BLANK, and the artwork
    // sits inside it at the flap offset (12.0 x 38.5 mm on a measured
    // Bottom-Lock carton). Composing the published chain with the placement
    // gives the artwork's own affine; without it every stamp lands at the
    // blank's corner instead of the die's. `artworkMatrix` throws rather than
    // approximate a chain it cannot express.
    const chain = chains.get(src.source_id);
    if (chain) {
      const place = opts.placeAffineFor
        ? opts.placeAffineFor(p)
        : blankToSheet(p.placed_vertices, p.rotation_deg);
      // One piece can need several stamps: a prepress tile is painted through
      // each of its members, and a resequenced panel order through each group
      // of panels that share a transform, clipped to the artwork it owns.
      const cartons = placementCartonCount(p);
      const stamps = artworkStamps(chain, place, cartons);
      if (stamps && stamps.length) {
        // Where each carton in this piece is CUT and FOLDED: the flapped
        // blank and prepress's panel creases, neither of which the uploaded
        // artwork says. Marks and the bleed knockout are per physical carton,
        // so every stamp names its carton and the first carries the cut.
        const cuts = new Map((placedCuts(result, src.source_id, place, cartons, p) || [])
          .map((c) => [c.member, c]));
        const pieceKey = p.editor_uid != null ? `e${p.editor_uid}` : `p${pieceIndex}`;
        const sent = new Set();
        return stamps.map((st) => {
          const key = `${pieceKey}:${st.member}`;
          const cut = cuts.get(st.member);
          const withCut = cut && !sent.has(key);
          if (withCut) sent.add(key);
          return {
            source_id: src.source_id,
            matrix: roundMatrix(st.matrix),
            ...(st.clip ? { clip_mm: st.clip.map(([x, y]) => [round9(x), round9(y)]) } : {}),
            carton: key,
            ...(withCut ? {
              cut: {
                outline_mm: cut.outline.map(([x, y]) => [round9(x), round9(y)]),
                folds_mm: cut.folds.map((seg) => seg.map(([x, y]) => [round9(x), round9(y)])),
              },
            } : {}),
          };
        });
      }
    }

    const xs = p.placed_vertices.map((v) => v[0]);
    const ys = p.placed_vertices.map((v) => v[1]);
    return [{
      source_id: src.source_id,
      x_mm: Math.min(...xs),                 // lower-left of placed die bbox (Y-up mm)
      y_mm: Math.min(...ys),
      rotation_deg: p.rotation_deg,          // raw MNE CCW value — no sign flip (spec §4.2)
    }];
  });

  return {
    sheet, sources, placements, marks, marks_on_neighbours: marksOnNeighbours, bleed,
    auto_bleed: autoBleed, output_intent: outputIntent, borders,
  };
}

/**
 * What each bleed `status` in X-Impose-Manifest means to an operator — a
 * carton's, or a source's (its cartons together); an edge of a carton's
 * printed outline says one of the first five (contract 1.12
 * `press_pdf.bleed.statuses` / `edge_statuses`; contract.test.js checks this
 * map covers every published status). `attention`: the status itself needs a
 * look. A `mixed` or `generated` row needs one too when any of it was withheld,
 * or skipped while auto bleed was on — pressReport works that out from the mm.
 */
export const BLEED_STATUS = {
  artwork: { label: 'Bleed from the artwork', tone: 'ok', attention: false },
  generated: { label: 'Bleed generated — edges stretched', tone: 'ok', attention: false },
  mixed: { label: 'Bleed — part artwork, part generated', tone: 'ok', attention: false },
  skipped: { label: 'No bleed — edges not stretched', tone: 'neutral', attention: false },
  withheld: { label: 'No bleed — artwork that is not the carton’s lies past its edge', tone: 'warn', attention: true },
  check_failed: { label: 'No bleed — the sheet did not show it', tone: 'warn', attention: true },
  error: { label: 'No bleed — analysis failed', tone: 'danger', attention: true },
  off: { label: 'Bleed off', tone: 'neutral', attention: false },
};

/**
 * Read the marks and bleed report out of the parsed X-Impose-Manifest.
 *
 * Null when there is neither (an older backend, or an unreadable header).
 * `marks.sources` rows: drawn / skipped / on neighbour per source, and whether
 * its marks come from its folds or from its outline only. `bleed.rows`: one
 * per source, unknown statuses shown and counted as needing attention.
 */
export function pressReport(manifest) {
  if (!manifest || typeof manifest !== 'object') return null;
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const m = manifest.marks && typeof manifest.marks === 'object' ? manifest.marks : null;
  const b = manifest.bleed && typeof manifest.bleed === 'object' ? manifest.bleed : null;
  if (!m && !b) return null;
  const marks = m ? {
    enabled: m.enabled !== false,
    mode: m.mode === 'print' ? 'print' : 'skip',
    drawn: num(m.drawn),
    skipped: num(m.skipped),
    onNeighbour: num(m.on_neighbour),
    offSheet: num(m.off_sheet),
    outlineOnly: Array.isArray(m.outline_only) ? m.outline_only.filter((s) => typeof s === 'string') : [],
    unresolved: Array.isArray(m.unresolved) ? m.unresolved : [],
    rows: Object.keys(m.sources || {}).map((sourceId) => {
      const r = m.sources[sourceId] || {};
      return {
        sourceId,
        cartons: num(r.cartons),
        drawn: num(r.drawn),
        skipped: num(r.skipped),
        onNeighbour: num(r.on_neighbour),
        folds: typeof r.folds === 'string' ? r.folds : 'outline_only',
      };
    }),
  } : null;
  const autoBleed = b ? b.auto_bleed !== false : true;
  const bleed = b ? {
    enabled: b.enabled !== false,
    autoBleed,
    bleedMm: num(b.bleed_mm),
    edgesTrimmed: b.edges_trimmed === true,
    rows: Object.keys(b.sources || {}).map((sourceId) => {
      const r = b.sources[sourceId] || {};
      const status = typeof r.status === 'string' ? r.status : 'unknown';
      const known = BLEED_STATUS[status]
        || { label: `Unknown bleed result "${status}"`, tone: 'warn', attention: true };
      const withheldMm = num(r.withheld_mm);
      const skippedMm = num(r.skipped_mm);
      // Some edge got no bleed it should have: withheld, or not stretched
      // although stretching was asked for (a curved edge, borders kept).
      const short = withheldMm > 0 || (autoBleed && skippedMm > 0);
      return {
        sourceId, status, label: known.label,
        tone: short && known.tone === 'ok' ? 'warn' : known.tone,
        attention: known.attention || short,
        cartons: num(r.cartons),
        artworkMm: num(r.artwork_mm), generatedMm: num(r.generated_mm),
        skippedMm, withheldMm,
        reason: typeof r.reason === 'string' ? r.reason : '',
      };
    }),
    // One row per physical carton, with its edges when the header carried them.
    cartons: (Array.isArray(b.cartons) ? b.cartons : []).map((c) => ({
      carton: String(c.carton), sourceId: String(c.source_id), status: String(c.status),
      verified: c.verified === true ? true : (c.verified === false ? false : null),
      edges: Array.isArray(c.edges) ? c.edges.map((e) => ({
        status: String(e.status), mm: num(e.mm), reason: typeof e.reason === 'string' ? e.reason : '',
      })) : [],
    })),
  } : null;
  const attention = (bleed ? bleed.rows.filter((r) => r.attention).length : 0)
    + (marks ? marks.unresolved.length : 0);
  return { marks, bleed, attention };
}

/**
 * What each `borders.sources[].status` means to an operator. `attention` marks
 * the outcomes where removal was asked for and did NOT happen — the carton was
 * plated with its borders, and the operator should know which one and why.
 */
export const BORDER_STATUS = {
  removed: { label: 'Borders removed', tone: 'ok', attention: false },
  none: { label: 'No borders found', tone: 'neutral', attention: false },
  kept: { label: 'Borders kept', tone: 'neutral', attention: false },
  not_tiled: { label: 'Borders left in — they do not tile the carton', tone: 'warn', attention: true },
  check_failed: { label: 'Borders left in — safety check failed', tone: 'warn', attention: true },
  error: { label: 'Borders left in — analysis failed', tone: 'danger', attention: true },
};

/**
 * Read the per-carton border report out of the parsed X-Impose-Manifest.
 *
 * Returns null when there is none — an older backend, or a header the browser
 * could not read (it must be CORS-exposed). Read defensively: an unknown status
 * is shown, and counted as needing attention, rather than dropped.
 *
 * @returns {null|{ mode: string, rows: Array<{ sourceId, status, label, tone,
 *   attention, removed, kept, dieline, reason }>, attention: number }}
 */
export function borderReport(manifest) {
  const b = manifest && manifest.borders;
  if (!b || typeof b !== 'object' || !b.sources || typeof b.sources !== 'object') return null;
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const rows = Object.keys(b.sources).map((sourceId) => {
    const s = b.sources[sourceId] || {};
    const status = typeof s.status === 'string' ? s.status : 'unknown';
    const known = BORDER_STATUS[status]
      || { label: `Unknown border result "${status}"`, tone: 'warn', attention: true };
    return {
      sourceId,
      status,
      label: known.label,
      tone: known.tone,
      attention: known.attention,
      removed: num(s.removed) ?? 0,
      kept: num(s.kept),
      dieline: num(s.dieline) ?? 0,
      reason: typeof s.reason === 'string' ? s.reason : '',
    };
  });
  return {
    mode: typeof b.mode === 'string' ? b.mode : 'remove',
    rows,
    attention: rows.filter((r) => r.attention).length,
  };
}

/**
 * Call POST /export/vector/pdf and return the press-ready PDF as a Blob plus
 * the parsed X-Impose-Manifest header (handover "Response").
 *
 * @param {Object} args
 * @param {string} args.apiBase
 * @param {Object} args.payload   from buildPressPdfPayload()
 * @param {Object<string,File>} args.filesBySourceId  one File per UNIQUE source_id
 * @returns {Promise<{ blob: Blob, manifest: Object|null }>}
 * @throws {VectorApiError} on non-2xx.
 */
export async function downloadPressPdf({ apiBase, payload, filesBySourceId, signal }) {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));

  // One file part per unique source_id (dedup — a carton ganged N times is
  // uploaded once; handover "The endpoint").
  const seen = new Set();
  for (const s of payload.sources) {
    if (seen.has(s.source_id)) continue;
    const file = filesBySourceId[s.source_id];
    if (!file) throw new Error(`downloadPressPdf: missing file for source_id "${s.source_id}"`);
    form.append(s.source_id, file);   // file part keyed by source_id
    seen.add(s.source_id);
  }

  const resp = await authFetch(`${apiBase}/export/vector/pdf`, { method: 'POST', body: form, signal });
  if (!resp.ok) throw await readError(resp);

  let manifest = null;
  const hdr = resp.headers.get('X-Impose-Manifest');
  if (hdr) { try { manifest = JSON.parse(hdr); } catch { /* header not JSON */ } }

  const blob = await resp.blob();
  return { blob, manifest };
}

/** Trigger a browser download of a Blob under the given filename. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Ask prepress to build the real tile for one carton (POST /export/vector/tile).
 *
 * The Layout Editor can slide two copies of a plain die together itself, and for
 * a die with no carton type that is the honest answer. It is NOT the answer for
 * a BL carton: prepress fuses two blanks and RESEQUENCES the panels of one of
 * them — the measured pair puts member B in variant RBLM/F, whose Right panel
 * moves 228 mm away from its siblings. That is interlocking geometry with rules
 * and configuration behind it, so prepress builds it and this fetches it.
 *
 * Synchronous: `pair` mode is direct geometry with no nest and no engine.
 *
 * @returns the TileResponse — tile outline, per-carton components, and the
 *   report entry carrying `transforms.tile_members` for the export chain.
 */
export async function requestPrepressTile({ apiBase, sourceId, vertices,
  cartonType = null, prepressConfig = null, signal }) {
  const body = { source_id: sourceId, vertices };
  if (cartonType) body.carton_type = cartonType;
  if (prepressConfig) body.prepress_config = prepressConfig;

  const resp = await authFetch(`${apiBase}/export/vector/tile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}
